import { supabase } from '@/lib/supabase';
import {
  type AppProposal,
  type BroadState,
  type CompanyQualificationSheet,
  type ConfigurationArea,
  type ConfigurationProposal,
  type HypothesisState,
  type ProposalDecision,
  type QualificationItem,
  type QualificationSectionKey,
  type QualificationStatus,
  type QualificationThemeKey,
  type ThemeDepth,
  type UsagePlace,
  type UsageProposal,
  type UsageRhythm,
  QUALIFICATION_SECTIONS,
  QUALIFICATION_THEME_LABELS,
  QUALIFICATION_THEMES,
  newQualificationId,
  parseQualificationSheet,
  qualificationIds,
} from '@shared/types/company-qualification';

export const SECTION_LABELS: Record<QualificationSectionKey, { label: string; hint: string }> = {
  product: { label: 'Produit', hint: 'Ce qui est vendu, comment ça marche, quel résultat, quelles preuves.' },
  offer: { label: 'Offre et prix', hint: 'Offres, prix, offre à pousser, ce qui vient après la première vente.' },
  icp: { label: 'Cible et acheteurs', hint: 'Secteur, taille, zone ; décideur, utilisateur, bloqueur ; exclusions ; pourquoi maintenant.' },
  competition: { label: 'Concurrence', hint: 'Ce que le client ferait sans vous, les alternatives réelles et le statu quo.' },
  site: { label: 'Site et message', hint: 'Promesse, cible affichée, preuves, appels à l’action, liens.' },
  tools: { label: 'Outils utilisés', hint: 'CRM, messagerie, agenda, téléphonie, enrichissement, IA ; ce qui est relié et ce qui ne l’est pas.' },
  crm: { label: 'CRM', hint: 'Volume, liens, qualité, données de test, origine des données, étapes face au processus réel.' },
  workflows: { label: 'Workflows', hint: 'Le travail réel, étape par étape : qui, quoi, avec quel outil, combien de temps.' },
  means: { label: 'Moyens et rythme', hint: 'Taille de l’équipe, temps disponible, canaux, envoi d’emails.' },
  objectives: { label: 'Objectifs', hint: 'Objectif chiffré, échéance, point de départ, volume par semaine, réussite vérifiable.' },
  pains: { label: 'Douleurs et goulots', hint: 'Par étape du travail : temps, oublis, blocages, cause et coût.' },
  people: { label: 'Personnes et adoption', hint: 'Sponsor, référent, utilisateurs, sceptique ; ce qui poussera ou retiendra.' },
  contradictions: { label: 'Contradictions à trancher', hint: 'Ce que disent le client, le site et le CRM quand ils ne concordent pas.' },
  reference_examples: { label: 'Exemples de référence', hint: 'Meilleurs clients, dernier gagné et perdu, bon et mauvais message.' },
};

export const THEME_LABELS: Record<QualificationThemeKey, { label: string; deliverable: string }> = {
  company: { label: QUALIFICATION_THEME_LABELS.company, deliverable: 'Ce qui est vendu, à quel prix, ce que dit le site.' },
  icp: { label: QUALIFICATION_THEME_LABELS.icp, deliverable: 'La cible fondée sur les clients réels, les exclusions, pourquoi maintenant.' },
  competition: { label: QUALIFICATION_THEME_LABELS.competition, deliverable: 'Les alternatives vues par l’acheteur, le statu quo, ce qui vous distingue.' },
  tools: { label: QUALIFICATION_THEME_LABELS.tools, deliverable: 'Les outils utilisés et reliés, le diagnostic chiffré du CRM.' },
  workflows: { label: QUALIFICATION_THEME_LABELS.workflows, deliverable: 'Le travail réel, étape par étape, et le temps qu’il prend.' },
  pains: { label: QUALIFICATION_THEME_LABELS.pains, deliverable: 'Les goulots classés, leur cause et leur coût.' },
  objectives: { label: QUALIFICATION_THEME_LABELS.objectives, deliverable: 'Le volume nécessaire par semaine, l’échéance, une réussite vérifiable.' },
  team: { label: QUALIFICATION_THEME_LABELS.team, deliverable: 'Les rôles, le temps disponible, ce qui poussera ou freinera l’usage.' },
};

export const DEPTH_LABELS: Record<ThemeDepth, string> = {
  surface: 'À approfondir',
  in_progress: 'En cours',
  deep: 'Approfondi',
};

export const BROAD_LABELS: Record<BroadState, string> = {
  not_started: 'À faire',
  in_progress: 'En cours',
  done: 'Faite',
};

export const HYPOTHESIS_STATE_LABELS: Record<HypothesisState, string> = {
  open: 'À tester',
  confirmed: 'Confirmée',
  refuted: 'Écartée',
};

export const STATUS_LABELS: Record<QualificationStatus, string> = {
  validated: 'Validé',
  observed: 'Constaté',
  hypothesis: 'Hypothèse',
  gap: 'Trou',
};

export const CONFIGURATION_AREA_LABELS: Record<ConfigurationArea, string> = {
  profile: 'Profil',
  objectives: 'Objectifs',
  crm: 'CRM',
  connections: 'Connexions',
  documents: 'Documents',
};

export const USAGE_RHYTHM_LABELS: Record<UsageRhythm, string> = {
  daily: 'Chaque jour',
  weekly: 'Chaque semaine',
  monthly: 'Chaque mois',
  once: 'Une fois',
};

export const USAGE_PLACE_LABELS: Record<UsagePlace, string> = {
  today: 'Mon travail',
  accounts: 'Comptes',
  apps: 'Applications',
  chat: 'Chat',
  forge: 'Forge',
  profile: 'Profil',
  objectives: 'Objectifs',
};

export const today = (): string => new Date().toISOString().slice(0, 10);

export interface StoredQualification {
  sheet: CompanyQualificationSheet;
  version: number;
  dropped: number;
  updatedAt: string | null;
}

/** The tenant's qualification (version 0 when none was saved yet). */
export const loadQualification = async (): Promise<StoredQualification> => {
  const { data, error } = await supabase.from('company_qualifications').select('sheet, version, updated_at').maybeSingle();
  if (error) throw new Error('La qualification n’est pas accessible pour le moment. Réessayez.');
  if (!data) return { ...parseQualificationSheet(null), version: 0, updatedAt: null };
  const { sheet, dropped } = parseQualificationSheet(data.sheet);
  return { sheet, dropped, version: data.version as number, updatedAt: data.updated_at as string };
};

export class QualificationConflictError extends Error {}

export const saveQualification = async (
  sheet: CompanyQualificationSheet,
  expectedVersion: number,
  source: 'manual' | 'restore' = 'manual',
  note?: string,
): Promise<number> => {
  const { data, error } = await supabase.rpc('save_company_qualification_q0', {
    p_expected_version: expectedVersion, p_sheet: sheet, p_source: source, p_note: note ?? null,
  });
  if (error) {
    if (error.code === '40001') throw new QualificationConflictError('La fiche a changé entre-temps : rechargez-la avant d’enregistrer.');
    if (error.code === '42501') throw new Error('Seuls les managers peuvent modifier la qualification.');
    throw new Error('L’enregistrement a échoué. Réessayez.');
  }
  return data as number;
};

/** A person's change validates the line: the analyst never sets « validé ». */
export const editItem = (
  sheet: CompanyQualificationSheet, section: QualificationSectionKey, itemId: string, text: string,
): CompanyQualificationSheet => ({
  ...sheet,
  sections: {
    ...sheet.sections,
    [section]: sheet.sections[section].map((item) => item.id === itemId
      ? { ...item, text: text.trim(), status: 'validated' as const, date: today() }
      : item),
  },
});

export const setItemStatus = (
  sheet: CompanyQualificationSheet, section: QualificationSectionKey, itemId: string, status: QualificationStatus,
): CompanyQualificationSheet => ({
  ...sheet,
  sections: {
    ...sheet.sections,
    [section]: sheet.sections[section].map((item) => item.id === itemId ? { ...item, status, date: today() } : item),
  },
});

export const addItem = (
  sheet: CompanyQualificationSheet, section: QualificationSectionKey, text: string,
): CompanyQualificationSheet => {
  const item: QualificationItem = {
    id: newQualificationId(section.replace(/_/g, '-'), qualificationIds(sheet)),
    text: text.trim(),
    status: 'validated',
    source: { kind: 'manual', label: 'Saisi dans la fiche' },
    date: today(),
  };
  return { ...sheet, sections: { ...sheet.sections, [section]: [...sheet.sections[section], item] } };
};

export const removeItem = (
  sheet: CompanyQualificationSheet, section: QualificationSectionKey, itemId: string,
): CompanyQualificationSheet => ({
  ...sheet,
  sections: { ...sheet.sections, [section]: sheet.sections[section].filter((item) => item.id !== itemId) },
});

type ProposalList = 'configuration' | 'apps' | 'usage';
type ProposalEntry = ConfigurationProposal | AppProposal | UsageProposal;

export const setProposalDecision = (
  sheet: CompanyQualificationSheet, list: ProposalList, entryId: string, decision: ProposalDecision,
): CompanyQualificationSheet => ({
  ...sheet,
  proposal: {
    ...sheet.proposal,
    [list]: (sheet.proposal[list] as ProposalEntry[]).map((entry) => entry.id === entryId ? { ...entry, decision } : entry),
  },
});

export const addProposalEntry = <L extends ProposalList>(
  sheet: CompanyQualificationSheet, list: L,
  entry: Omit<CompanyQualificationSheet['proposal'][L][number], 'id' | 'decision'>,
): CompanyQualificationSheet => {
  const prefix = list === 'configuration' ? 'config' : list === 'apps' ? 'app' : 'usage';
  const created = { ...entry, id: newQualificationId(prefix, qualificationIds(sheet)), decision: 'accepted' as const };
  return { ...sheet, proposal: { ...sheet.proposal, [list]: [...sheet.proposal[list], created] } };
};

export const removeProposalEntry = (
  sheet: CompanyQualificationSheet, list: ProposalList, entryId: string,
): CompanyQualificationSheet => ({
  ...sheet,
  proposal: { ...sheet.proposal, [list]: (sheet.proposal[list] as ProposalEntry[]).filter((entry) => entry.id !== entryId) },
});

export const countByStatus = (sheet: CompanyQualificationSheet): Record<QualificationStatus, number> => {
  const counts: Record<QualificationStatus, number> = { validated: 0, observed: 0, hypothesis: 0, gap: 0 };
  for (const key of QUALIFICATION_SECTIONS) for (const item of sheet.sections[key]) counts[item.status] += 1;
  return counts;
};

export const themeCounts = (
  sheet: CompanyQualificationSheet, theme: QualificationThemeKey,
): Record<QualificationStatus, number> => {
  const counts: Record<QualificationStatus, number> = { validated: 0, observed: 0, hypothesis: 0, gap: 0 };
  const sections = QUALIFICATION_THEMES.find(({ key }) => key === theme)?.sections ?? [];
  for (const section of sections) for (const item of sheet.sections[section]) counts[item.status] += 1;
  return counts;
};

/** Themes with at least one line: the broad qualification has touched them. */
export const coveredThemes = (sheet: CompanyQualificationSheet): number =>
  QUALIFICATION_THEMES.filter(({ sections }) => sections.some((section) => sheet.sections[section].length > 0)).length;

export const setTheme = (
  sheet: CompanyQualificationSheet, theme: QualificationThemeKey,
  change: Partial<CompanyQualificationSheet['themes'][QualificationThemeKey]>,
): CompanyQualificationSheet => ({ ...sheet, themes: { ...sheet.themes, [theme]: { ...sheet.themes[theme], ...change } } });

/** A person decides a working hypothesis by hand: the analyst will not change it afterwards. */
export const setHypothesisState = (sheet: CompanyQualificationSheet, id: string, state: HypothesisState): CompanyQualificationSheet => ({
  ...sheet,
  hypotheses: sheet.hypotheses.map((entry) => entry.id !== id ? entry : {
    ...entry, state, date: today(),
    evidence: state === 'open' ? undefined : { kind: 'manual', label: `Tranchée par vous le ${today()}` },
  }),
});

export const setBroadState = (sheet: CompanyQualificationSheet, state: BroadState): CompanyQualificationSheet =>
  ({ ...sheet, broad: { state } });
